import { groupBy, last, first, takeRight, orderBy } from 'lodash'
import $date from '@services/date/index.js'
import router from '@/router/index.js'
import { success, fail } from '@/helpers/result-helper'
import { isSuccess } from '@/helpers/http-status-helpers'
import { MessageDirection } from '@/constants/message/MessageDirection'
import toast from '@services/toasts/index.js'
import { RecipientType } from '@/constants/message/RecipientType'
import EmbedContext from '@/constants/core/EmbedContext'

const getDefaultState = () => {
  return {
    loadingCount: 0,
    crudLoadingCount: 0,
    recentMessagesLoadingCount: 0,
    candidateMessagesLoadingCount: 0,
    messages: [],
    current: null, // contains ID of candidate/contact that the user is currently viewing to set incoming messages as read
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,

  getters: {
    isLoadingMessages: (state) => state.loadingCount > 0,
    isLoadingMessageCRUD: (state) => state.crudLoadingCount > 0,
    isLoadingRecentMessages: (state) => state.recentMessagesLoadingCount > 0,
    isLoadingCandidateMessages: (state) =>
      state.candidateMessagesLoadingCount > 0,
    /**
     * Populates candidate-list.vue
     */
    getSummaryMessageList: (state) => {
      const mappedList = state.messages.map((x) => {
        let preview = ''
        let timestamp = ''

        // Display preview text & timestamp only if messages are available
        if (x.messages && x.messages.length >= 1) {
          const latestMessage = first(x.messages)

          // Appends 'You:' to preview text if message was sent by current user
          // TODO: Check senderId === currentUser.id. Me no longer means from logged in user
          preview = `${latestMessage.me ? 'Outbound:' : ''} ${
            latestMessage.content
          }`
          timestamp = latestMessage.timestamp
        }

        return {
          id: x.id,
          name: x.name,
          img: x.img,
          contactType: x.contactType,
          unread:
            x.messages.length >= 1 &&
            !last(x.messages).readBy &&
            !last(x.messages).me,
          preview,
          timestamp,
        }
      })

      return mappedList
    },

    getMessagesByCandidateId: (state) => (id) => {
      return state.messages.find((x) => x.id === id)
    },

    countNewMessages: (state) => {
      if (state.messages.length === 0) return 0
      return state.messages.filter((x) => {
        // .some((y) => !y.readBy && !y.me)
        if (x.messages.length === 0) return false
        const lastMessage = last(x.messages)
        return !lastMessage.readBy && !lastMessage.me
      }).length
    },

    hasMoreCandidateMessages: (state) => (id, limit) => {
      const candidateMessages = state.messages.find((x) => x.id === id)

      if (!candidateMessages) return false

      return candidateMessages.messages.length - limit > 0
    },

    getCandidateMessages:
      (state) =>
      (id, limit = 50) => {
        const ungroupedMessages = state.messages.find((x) => x.id === id)

        if (!ungroupedMessages) return null

        const candidateMessages = orderBy(
          ungroupedMessages.messages,
          [
            function (message) {
              return $date(message.timestamp)
            },
          ],
          ['asc']
        )

        // Group messages by date ( { 'Friday, 16 July 2021': [{ msg1, msg2 }] })
        const groupedMessages = groupBy(
          takeRight(candidateMessages, limit),
          (message) => {
            const day = $date(message.timestamp)

            // Convert keys to relative form for today and yesterday
            if (day.isToday()) return 'Today'
            else if (day.isYesterday()) return 'Yesterday'

            return day.format('dddd, D MMM YYYY')
          }
        )

        return {
          contact: {
            id: ungroupedMessages.id,
            name: ungroupedMessages.name,
            img: ungroupedMessages.img,
          },
          messages: groupedMessages,
        }
      },

    currentlyViewing: (state) => state.current,
    /**
     * In certain scenarios we want to prevent the app from loading all candidate/contact
     * SMS messages. For example, if we're embedding 2-way SMS into the candidate view
     * in RR (Impala), we don't want to load unnecessary data and override what's already
     * been loaded.
     */
    preventLoadingAllMessages: (state, getters, rootState, rootGetters) => {
      const embedContext = rootGetters.getEmbedContext

      switch (embedContext) {
        case EmbedContext.ReliefRosterCandidateView:
          return true
        default:
          return false
      }
    },
  },

  mutations: {
    CLEAR_MESSAGES(state) {
      state.messages = []
    },
    INSERT_MESSAGE(state, payload) {
      const found = state.messages.find((x) => x.id === payload.recipientId)
      if (found) found.messages.push(payload.message)
    },
    UPSERT_MESSAGES(state, dPayload) {
      let found
      let messages = dPayload
      if (!Array.isArray(messages)) messages = [dPayload]

      for (const message of messages) {
        found = state.messages.find((x) => x.id === message.id)

        if (!found) {
          message.isDirty = true
          state.messages.push(message)
        } else {
          found.isDirty = false
          Object.assign(found, message)
        }
      }
    },
    SET_CURRENT(state, id) {
      state.current = id
    },

    INSERT_INBOUND_MESSAGE(state, payload) {
      const found = state.messages.find((x) => x.id === payload.id)

      if (found) {
        found.unread = true
        found.messages.push({
          id: payload.message.id,
          content: payload.message.content,
          senderId: payload.id,
          senderName: `${payload.firstName} ${payload.lastName}`,
          me: false,
          timestamp: payload.message.dateSentUtc,
          readBy: null,
        })
      } else {
        state.messages.push({
          id: payload.id,
          name: `${payload.firstName} ${payload.lastName}`,
          type: payload.type,
          img: null,
          unread: true,
          messages: [
            {
              id: payload.message.id,
              content: payload.message.content,
              senderId: payload.id,
              senderName: `${payload.firstName} ${payload.lastName}`,
              me: false,
              timestamp: payload.message.dateSentUtc,
              readBy: null,
            },
          ],
        })
      }
    },

    SET_MESSAGE_TO_READ(state, { id, adminUserId }) {
      // Attempt to find message in store, abort if not found
      const candidateMessage = state.messages.find((x) => x.id === id)

      if (
        !candidateMessage ||
        !candidateMessage.messages ||
        candidateMessage.messages.length === 0
      )
        return

      // Filter out messages to list only candidate/contact messages
      const filteredMessages = candidateMessage.messages.filter((x) => !x.me)

      if (filteredMessages.length === 0) return

      // Set last message to read by current user
      last(filteredMessages).readBy = adminUserId
    },
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    START_LOADING_RECENT_MESSAGES(state) {
      state.recentMessagesLoadingCount++
    },
    FINISH_LOADING_RECENT_MESSAGES(state) {
      state.recentMessagesLoadingCount--
    },
    START_LOADING_CANDIDATE_MESSAGES(state) {
      state.candidateMessagesLoadingCount++
    },
    FINISH_LOADING_CANDIDATE_MESSAGES(state) {
      state.candidateMessagesLoadingCount--
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
  },

  actions: {
    redirectAndOpenCandidateMessages(context, id) {
      router.push({ name: 'message', query: { cid: id } })
    },
    mapCandidateMessage({ rootGetters }, payload) {
      if (!payload || !payload.messages || payload.messages.length === 0)
        return []

      const messages = []

      for (const message of payload.messages) {
        if (message.messageDirection === MessageDirection.INBOUND) {
          messages.push({
            id: message.id,
            content: message.content,
            type: message.type,
            me: false, // Any inbound message should be shown on the left hand side
            senderId: payload.id,
            senderName: payload.name,
            timestamp: message.dateCreatedUTC,
            readBy: message.readAdminUserID,
          })
        } else if (message.messageDirection === MessageDirection.OUTBOUND) {
          const hasSenderId = !!message.senderAdminUserID
          const isMe =
            rootGetters['auth/currentUser'].id.toString() ===
            message.senderAdminUserID // Check if send is logged in user

          let senderName = 'Outbound From Anzuk'

          if (isMe) {
            senderName = rootGetters['auth/currentUserFullName']
          } else if (hasSenderId) {
            let user = rootGetters['users/user'](message.senderAdminUserID)

            // Handle case where team member could not be found
            if (!user)
              user = {
                displayName: 'Unknown User',
              }

            senderName = user?.displayName || user?.name
          }

          messages.push({
            id: message.id,
            content: message.content,
            type: message.type,
            me: true, // Any outbound message should be shown on the right hand side
            senderId: message.senderAdminUserID,
            senderName,
            timestamp: message.dateCreatedUTC,
            readBy: message.readAdminUserID,
          })
        }
      }

      return messages
    },

    async loadMyTeamMessages({ commit, dispatch }) {
      commit('CLEAR_MESSAGES')
      commit('START_LOADING_RECENT_MESSAGES')

      try {
        const response = await this.$api.candidate.getMyTeamSMSs()

        if (isSuccess(response.status)) {
          const mappedMessages = []
          if (response.data.length > 0) {
            // Map response from server
            for (const message of response.data) {
              message.messages = await dispatch('mapCandidateMessage', message)
              mappedMessages.push(message)
            }
          }

          commit('UPSERT_MESSAGES', mappedMessages)
          return success(mappedMessages)
        }
      } catch (ex) {
        console.error(ex)
        toast.show("Couldn't load inbound messages")
        return fail(ex.response)
      } finally {
        commit('FINISH_LOADING_RECENT_MESSAGES')
      }
    },
    async loadMessages({ commit, dispatch, getters }) {
      if (getters.preventLoadingAllMessages) return success([])

      commit('CLEAR_MESSAGES')
      commit('START_LOADING_RECENT_MESSAGES')
      try {
        const response = await this.$api.candidate.getMyCandidateSMSs()

        if (isSuccess(response.status)) {
          const mappedMessages = []
          if (response.data.length > 0) {
            // Map response from server
            for (const message of response.data) {
              message.messages = await dispatch('mapCandidateMessage', message)
              mappedMessages.push(message)
            }
          }

          commit('UPSERT_MESSAGES', mappedMessages)
          return success(mappedMessages)
        }
      } catch (ex) {
        console.error(ex)
        toast.show("Couldn't load inbound messages")
        return fail(ex.response)
      } finally {
        commit('FINISH_LOADING_RECENT_MESSAGES')
      }
    },
    async getCandidateMessagesById({ commit, getters, dispatch }, id) {
      // If messages already exist in store, abort
      const found = getters.getMessagesByCandidateId(id)
      if (found && !found.isDirty) return success()

      commit('START_LOADING_CANDIDATE_MESSAGES')

      try {
        // Retrieve messages from api
        const response = await this.$api.candidate.getCandidateMessagesById(id)

        if (isSuccess(response.status)) {
          if (!response.data || response.data.length === 0) return fail()
          const cMessages = response.data
          cMessages.messages = await dispatch('mapCandidateMessage', cMessages)

          commit('UPSERT_MESSAGES', cMessages)
          return success(cMessages)
        }
      } catch (ex) {
        toast.error('There was an issue loading this candidates messages')
        return fail(ex.response)
      } finally {
        commit('FINISH_LOADING_CANDIDATE_MESSAGES')
      }
    },
    setCurrentlyViewedCandidateMessages({ commit }, id) {
      commit('SET_CURRENT', id)
    },
    /**
     * Sends an SMS message to a candidate/contact
     * @param {String} recipientId Id of recipient
     * @param {String} message Message to send to them
     * @returns success or fail object
     */
    async sendMessage(
      { commit, rootGetters },
      { recipientId = '', message = '', type = RecipientType.CANDIDATE }
    ) {
      if (!recipientId || !message) return fail()

      commit('START_LOADING_CRUD')

      try {
        const response = await this.$api[type.toLowerCase()].sendMessage({
          id: recipientId,
          message: message,
        })

        if (isSuccess(response.status)) {
          const messageResponse = response.data

          messageResponse.message = {
            ...messageResponse.message,
            ...{
              me: true,
              senderName: rootGetters['auth/currentUser'].name,
            },
          }

          // Save returned message to store
          commit('INSERT_MESSAGE', messageResponse)

          return success()
        }
      } catch (ex) {
        console.error(ex)
        toast.show('Oops.. something went wrong')
        return fail(ex.response)
      } finally {
        commit('FINISH_LOADING_CRUD')
      }
    },
    async setMessageToRead({ commit, rootGetters }, payload) {
      try {
        const response = await this.$api.candidate.readMessages({
          messageIds: [payload.message.id],
          adminUserId: rootGetters['auth/currentUser'].id,
        })

        if (isSuccess(response.status)) {
          // Save read receipt in store
          commit('SET_MESSAGE_TO_READ', {
            id: payload.id,
            adminUserId: rootGetters['auth/currentUser'].id,
          })

          return success(response.data)
        }

        return fail()
      } catch (ex) {
        console.error(ex)
        console.warn(
          'Unable to set read receipt for message Id',
          payload.message.id
        )
        return fail(ex.response)
      }
    },
    async setLastMessageToRead({ commit, getters, dispatch }, candidateId) {
      const found = getters.getMessagesByCandidateId(candidateId)

      if (!found) return fail()

      return dispatch('setMessageToRead', {
        id: found.id,
        message: last(found.messages),
      })
    },
    insertInboundMessage({ commit, dispatch }, message) {
      commit('INSERT_INBOUND_MESSAGE', message)

      // Create new notification
      dispatch('notification/createNotification', message, { root: true })
    },
    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
